import React, { useEffect, useRef, useState } from 'react';
import { useApiReload } from '../../context/apiRelaod';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/authContext';
import NotificationAudioRecorder from '../../pages/AudioNotificationRecord';
import { InProgress } from '../../services/apiService';
import { useAudioRecording, RecordingStatus } from '../../context/AudioRecordingContext';
import Tooltip from './Tooltip';

const NotificationDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [countNumber, setCountNumber] = useState(0);
  const [inProgressItems, setInProgressItems] = useState<any[]>([]);
  const [prevInProgressItems, setPrevInProgressItems] = useState<any[]>([]);
  const { response_Inprogress, call_InProgress } = InProgress();
  
  // Use the new dictionary-based context
  const { recordings, activeRecordingId } = useAudioRecording();
  const { patientID, setReloadNotes } = useApiReload();
  
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const { onLogout } = useAuth();
  
  // Set up polling for in-progress uploads
  useEffect(() => {
    // Define the fetch function
    const fetchInProgressData = () => {
      call_InProgress().subscribe({
        next(value) {
          setInProgressItems(value?.data || []);
        },
        error: (e: any) => {
          if (e?.status === 409) {
            onLogout(e?.status);
          }
          const toastId = 'api-error';
          if (!toast.isActive(toastId)) {
            toast.error(e?.data?.msg || e.message, { toastId });
          }
        },
      });
    };
    
    // Initial fetch
    fetchInProgressData();
    
    // Set up polling
    const intervalId = setInterval(fetchInProgressData, 15000);
    
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // Track changes in inProgressItems
  useEffect(() => {
    // Check if any items were removed (completed)
    if (prevInProgressItems.length > inProgressItems.length) {
      // Find which items were removed (completed)
      const completedItems = prevInProgressItems.filter(prevItem => 
        !inProgressItems.some(currItem => currItem.id === prevItem.id)
      );
      
      if (completedItems.length > 0) {
        // Only trigger reload if we're on the same patient page as the completed note
        // and not navigating between patients
        const completedPatientIds = completedItems.map(item => item.patientId);
        
        // If we're viewing a patient that has a completed note, trigger reload
        if (patientID && completedPatientIds.includes(patientID.id)) {
          setReloadNotes(true);
        }
      }
    }
    
    // Update prevInProgressItems for next comparison
    setPrevInProgressItems(inProgressItems);
  }, [inProgressItems, prevInProgressItems, setReloadNotes, patientID]);
  
  // Update notification count when data changes
  useEffect(() => {
    const inProgressCount = inProgressItems.length;
    const audioCount = Object.keys(recordings).length;
    setCountNumber(inProgressCount + audioCount);
  }, [inProgressItems, recordings]);
  
  // Handle click outside to close dropdown
  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      buttonRef.current &&
      !buttonRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };
  
  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);
  
  // Check if any recording is active (RECORDING status)
  const isAnyRecordingActive = Object.values(recordings).some(
    audio => audio.status === RecordingStatus.RECORDING
  );
  
  // Convert recordings dictionary to array for mapping
  const recordingsArray = Object.values(recordings);
  const recordingsCount = recordingsArray.length;
  
  return (
    <div className='relative'>
      <Tooltip text='Notifications' placement='bottom-end'>
        <span>
          {/* Notification Icon */}
          <button
            ref={buttonRef}
            onClick={() => setIsOpen((prev) => !prev)}
            className='relative rounded-full bg-gray-100 p-2'
            aria-label={`Notifications ${countNumber > 0 ? `(${countNumber})` : ''}`}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              fill='#000000'
              height='20px'
              width='20px'
              version='1.1'
              viewBox='0 0 611.999 611.999'
            >
              <g>
                <g>
                  <g>
                    <path d='M570.107,500.254c-65.037-29.371-67.511-155.441-67.559-158.622v-84.578c0-81.402-49.742-151.399-120.427-181.203     C381.969,34,347.883,0,306.001,0c-41.883,0-75.968,34.002-76.121,75.849c-70.682,29.804-120.425,99.801-120.425,181.203v84.578     c-0.046,3.181-2.522,129.251-67.561,158.622c-7.409,3.347-11.481,11.412-9.768,19.36c1.711,7.949,8.74,13.626,16.871,13.626     h164.88c3.38,18.594,12.172,35.892,25.619,49.903c17.86,18.608,41.479,28.856,66.502,28.856     c25.025,0,48.644-10.248,66.502-28.856c13.449-14.012,22.241-31.311,25.619-49.903h164.88c8.131,0,15.159-5.676,16.872-13.626     C581.586,511.664,577.516,503.6,570.107,500.254z M484.434,439.859c6.837,20.728,16.518,41.544,30.246,58.866H97.32     c13.726-17.32,23.407-38.135,30.244-58.866H484.434z M306.001,34.515c18.945,0,34.963,12.73,39.975,30.082     c-12.912-2.678-26.282-4.09-39.975-4.09s-27.063,1.411-39.975,4.09C271.039,47.246,287.057,34.515,306.001,34.515z      M143.97,341.736v-84.685c0-89.343,72.686-162.029,162.031-162.029s162.031,72.686,162.031,162.029v84.826     c0.023,2.596,0.427,29.879,7.303,63.465H136.663C143.543,371.724,143.949,344.393,143.97,341.736z M306.001,577.485     c-26.341,0-49.33-18.992-56.709-44.246h113.416C355.329,558.493,332.344,577.485,306.001,577.485z' />
                    <path d='M306.001,119.235c-74.25,0-134.657,60.405-134.657,134.654c0,9.531,7.727,17.258,17.258,17.258     c9.531,0,17.258-7.727,17.258-17.258c0-55.217,44.923-100.139,100.142-100.139c9.531,0,17.258-7.727,17.258-17.258     C323.259,126.96,315.532,119.235,306.001,119.235z' />
                  </g>
                </g>
              </g>
            </svg>
            
            {/* Notification Badge */}
            {countNumber > 0 && (
              <span className='absolute right-0 top-0 inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-xs font-bold text-white'>
                {countNumber}
              </span>
            )}
            
            {/* Recording Indicator - Show if ANY recording is active */}
            {isAnyRecordingActive && (
              <span className='absolute bottom-0.5 left-0 h-3 w-3 rounded-full border-2 border-white bg-red-500 animate-pulse'>
                {' '}
              </span>
            )}
          </button>
        </span>
      </Tooltip>
      
      {/* Dropdown Content */}
      <div
        ref={dropdownRef}
        className={`absolute ${isOpen ? '' : 'hidden'} right-0 mt-2 w-96 rounded-lg border border-gray-200 bg-white shadow-md z-50`}
      >
        <div className='border-b p-3'>
          <div className="flex justify-between items-center">
            <span className="font-semibold text-gray-700">
              {countNumber > 0 ? 'Notifications' : 'No notifications'}
            </span>
            
            {recordingsCount > 0 && (
              <span className="text-xs text-blue-600 font-medium">
                {recordingsCount}/3 recordings
              </span>
            )}
          </div>
        </div>
        
        <ul className='max-h-96 no-scrollbar overflow-x-hidden overflow-y-auto'>
          {/* Audio Recordings - Show these first */}
          {recordingsCount > 0 && (
            <li className="py-1 px-2 bg-gray-50">
              <h3 className="text-xs font-medium text-gray-500 uppercase">Active Recordings</h3>
            </li>
          )}
          
          {recordingsCount > 0 &&
            recordingsArray.map((item, index) => (
              <li
                className='border-b-2 p-2 hover:bg-gray-100'
                key={`audio-${item.patientId}-${item.Soapid}-${index}`}
              >
                <NotificationAudioRecorder
                  key={`recorder-${item.patientId}-${item.Soapid}-${index}`}
                  audioData={item}
                />
              </li>
            ))}
            
          {/* In-Progress Uploads */}
          {inProgressItems.length > 0 && (
            <li className="py-1 px-2 bg-gray-50">
              <h3 className="text-xs font-medium text-gray-500 uppercase">Processing</h3>
            </li>
          )}
          
          {inProgressItems.map((note: any, index: number) => (
            <li className='border-b-2 p-2 hover:bg-gray-100' key={`inprogress-${index}`}>
              <div className='flex-col gap-3'>
                <p className='text-sm capitalize'>
                  {note.noteType} : {note.patientName}
                </p>
                {note.progress_status === 'IN_PROCESS' ? (
                  <>
                    <span className='text-md'>
                      Status: <b>Processing...</b>
                    </span>
                    <div className='loader-line'></div>
                  </>
                ) : (
                  <> no soap list found </>
                )}
              </div>
            </li>
          ))}
            
          {/* Empty state */}
          {countNumber === 0 && (
            <li className='p-4 text-center text-gray-500'>
              No notifications to display
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default NotificationDropdown;